import type { ReactNode } from "react";

export type TagTone = "neutral" | "positive" | "negative" | "dark";
export type TagSize = "sm" | "md";

const BASE = "inline-flex items-center rounded-[4px] font-medium whitespace-nowrap";

export const TAG_TONE_CLASSES: Record<TagTone, string> = {
  neutral: "bg-grey-100 text-grey-900",
  positive: "bg-green-100 text-green-700",
  negative: "bg-red-100 text-red-600",
  dark: "bg-grey-950 text-white",
};

export const TAG_SIZE_CLASSES: Record<TagSize, string> = {
  sm: "h-6 px-2 gap-1.5 text-tiny",
  md: "px-2.5 py-1 gap-1 text-small",
};

export const TAG_BASE_CLASS = BASE;

type Props = {
  tone?: TagTone;
  size?: TagSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  children: ReactNode;
};

/**
 * Small inline tag for category, status, or range labels.
 *
 * Display-only by design. For interactive triggers that should look like a
 * tag (e.g. a dropdown opener), compose a `<button>` using the exported
 * `TAG_BASE_CLASS`, `TAG_SIZE_CLASSES`, and `TAG_TONE_CLASSES` so the visual
 * stays in sync with `Tag`.
 */
export function Tag({ tone = "neutral", size = "sm", leadingIcon, trailingIcon, children }: Props) {
  return (
    <span className={`${BASE} ${TAG_SIZE_CLASSES[size]} ${TAG_TONE_CLASSES[tone]}`}>
      {leadingIcon}
      <span>{children}</span>
      {trailingIcon}
    </span>
  );
}
